/**
 * The two-week pay-cycle words, and the regions that say them differently
 * (PAY-3372).
 *
 * "Fortnight" is how Australia and New Zealand name a two-week period, but a
 * US or Canadian payroll team does not use the word. So for these two terms
 * only, the DEFAULT follows the account's `region` setting. It is read at
 * render time, the way units and currency already are. Nothing is stored for
 * these two terms unless someone types a value in Settings > Terminology; a
 * blank field stays blank, and an explicit value wins over the region.
 *
 * This lives in `@ci/shared` because two packages need the SAME answer: the web
 * app's terminology merge, which decides what every surface renders, and the
 * Terminology settings form, which shows the region default as the placeholder
 * behind an empty field. A second copy in either place would drift.
 *
 * Keep this module free of React and of other `@ci/*` packages (it imports
 * only `./currency`): it is bundled into `apps/dev-tool` and trigger.dev
 * through the web app's terminology utils.
 *
 * Why this is not `apps/web/lib/pay-rate-processing/utils/market-terminology.ts`:
 *
 * - That map is app-local, and the Terminology form in `@ci/team-accounts`
 *   needs this answer too.
 * - Its US_CA map renames unrelated vocabulary ("Public Holiday" becomes
 *   "Federal Holiday", "Casual" becomes "Temporary/On-Call"), which needs its
 *   own product decision.
 * - Its `regionToMarket` substring-matches a free-text region. This reads the
 *   `{ value }` setting through `parseRegionSetting`, the same parser currency
 *   and units use.
 *
 * If more terms start to vary by region, add them to a table like this one,
 * not to `market-terminology`, so that Settings > Terminology can still
 * override every one of them.
 */
import type { AccountRegion } from './currency';

export const PAY_CYCLE_TERM_KEYS = ['fortnight', 'fortnightly'] as const;

export type PayCycleTermKey = (typeof PAY_CYCLE_TERM_KEYS)[number];

export type PayCycleTerms = Readonly<Record<PayCycleTermKey, string>>;

/**
 * AU, NZ, EU, and any account that has never saved a region. The noun
 * ("Fortnight total") and the adjective ("Fortnightly Overtime") are separate
 * terms because no single word fills both slots in US English.
 */
export const DEFAULT_PAY_CYCLE_TERMS: PayCycleTerms = Object.freeze({
  fortnight: 'Fortnight',
  fortnightly: 'Fortnightly',
});

/**
 * "Biweekly" is the adjective US and Canadian payroll uses. The noun is
 * "Two-week period" rather than "biweek", which is not a word, and it does not
 * have the ambiguity of "biweekly", which some readers take to mean twice a
 * week.
 */
const NORTH_AMERICAN_PAY_CYCLE_TERMS: PayCycleTerms = Object.freeze({
  fortnight: 'Two-week period',
  fortnightly: 'Biweekly',
});

const REGION_PAY_CYCLE_TERMS: Readonly<
  Partial<Record<AccountRegion, PayCycleTerms>>
> = {
  US: NORTH_AMERICAN_PAY_CYCLE_TERMS,
  CA: NORTH_AMERICAN_PAY_CYCLE_TERMS,
};

/**
 * Only the region's overrides, or an empty map for a region that has none.
 * This goes UNDER the account's own terminology, never over it.
 *
 * @param region - the PARSED region (`parseRegionSetting` of the stored
 *   setting). Parse at the boundary where the setting is read, not here.
 */
export function regionPayCycleTerms(
  region: AccountRegion | null,
): Partial<PayCycleTerms> {
  return (region ? REGION_PAY_CYCLE_TERMS[region] : undefined) ?? {};
}

/**
 * What the two terms read for a region with nothing saved against them. This
 * is the wording an empty settings field falls back to.
 *
 * @param region - the PARSED region, as for {@link regionPayCycleTerms}
 */
export function payCycleTermsForRegion(
  region: AccountRegion | null,
): PayCycleTerms {
  return { ...DEFAULT_PAY_CYCLE_TERMS, ...regionPayCycleTerms(region) };
}
